import React, { useState, useEffect } from 'react';
import {
  ThemeProvider,
  createTheme,
  CssBaseline,
  Box,
  Drawer,
  AppBar,
  Toolbar,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Divider,
  IconButton,
  Button,
} from '@mui/material';
import {
  Dashboard,
  Sensors,
  Warning,
  ElectricBolt,
  Settings,
  NotificationsNone,
  AccountCircle,
  Menu as MenuIcon,
  Logout,
} from '@mui/icons-material';
import DashboardPage from './components/DashboardPage';
import SensorDataPage from './components/SensorDataPage';
import AlertsPage from './components/AlertsPage';
import DeviceControlPage from './components/DeviceControlPage';
import SettingsPage from './components/SettingsPage';
import LoginPage from './components/LoginPage';
import { log } from './utils/logger';
import websocketService from './services/websocket';
import { AuthProvider, useAuth } from './hooks/useAuth';
import { TokenManager } from './utils/tokenManager';

// UI 디자인에 맞는 핑크 테마
const theme = createTheme({
  palette: {
    primary: {
      main: '#FF6B9D', // 핑크 메인 컬러
      light: '#FF8FB3',
      dark: '#E91E63',
    },
    secondary: {
      main: '#FFF',
    },
    background: {
      default: '#F8F9FA',
      paper: '#FFFFFF',
    },
    text: {
      primary: '#333333',
      secondary: '#666666',
    },
  },
  typography: {
    fontFamily: '"Noto Sans KR", "Roboto", "Arial", sans-serif',
    h6: {
      fontWeight: 600,
    },
  },
  shape: {
    borderRadius: 12,
  },
});

const drawerWidth = 240;

const menuItems = [
  { id: 'dashboard', label: '대시보드', icon: <Dashboard />, component: DashboardPage },
  { id: 'sensors', label: '센서 데이터', icon: <Sensors />, component: SensorDataPage },
  { id: 'alerts', label: '알림 및 경고', icon: <Warning />, component: AlertsPage },
  { id: 'devices', label: '에너지 및 운영 관리', icon: <ElectricBolt />, component: DeviceControlPage },
  { id: 'settings', label: '설정', icon: <Settings />, component: SettingsPage },
];

const MainApp: React.FC = () => {
  const [selectedPage, setSelectedPage] = useState('dashboard');
  const [mobileOpen, setMobileOpen] = useState(false);
  const { isAuthenticated, user, login, logout, isLoading } = useAuth();

  React.useEffect(() => {
    log.info('GoodFarmer App initialized', { version: '1.0.0' });
    
    // TokenManager 초기화
    TokenManager.initialize();

    // 로그인된 상태에서만 WebSocket 연결
    if (isAuthenticated) {
      // WebSocket 연결 시작
      websocketService.connect();

      // WebSocket 이벤트 리스너 등록
      const handleConnected = () => {
        log.info('WebSocket connected successfully');
      };

      const handleDisconnected = () => {
        log.warn('WebSocket disconnected');
      };

      const handleError = (error: any) => {
        log.error('WebSocket error', error);
      };

      websocketService.on('connected', handleConnected);
      websocketService.on('disconnected', handleDisconnected);
      websocketService.on('error', handleError);

      // 컴포넌트 언마운트 시 정리
      return () => {
        websocketService.off('connected', handleConnected);
        websocketService.off('disconnected', handleDisconnected);
        websocketService.off('error', handleError);
        websocketService.disconnect();
      };
    }
  }, [isAuthenticated]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handlePageChange = (pageId: string) => {
    setSelectedPage(pageId);
    log.debug('Page changed', { page: pageId });
  };

  const handleLogin = async (userId: string, password: string, rememberMe: boolean = false) => {
    try {
      await login(userId, password, rememberMe);
      log.info('User logged in');
    } catch (error) {
      log.error('Login failed', error);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      setSelectedPage('dashboard');
      websocketService.disconnect();
      log.info('User logged out');
    } catch (error) {
      log.error('Logout failed', error);
    }
  };

  // 로딩 중일 때
  if (isLoading) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
          <Typography>로딩 중...</Typography>
        </Box>
      </ThemeProvider>
    );
  }

  // 로그인되지 않은 경우 로그인 페이지 표시
  if (!isAuthenticated) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <LoginPage onLogin={handleLogin} />
      </ThemeProvider>
    );
  }

  const drawer = (
    <Box sx={{ height: '100%', bgcolor: 'primary.main', color: 'white' }}>
      {/* Logo & Brand */}
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 1 }}>
          SMART FARM
        </Typography>
      </Box>

      <Divider sx={{ borderColor: 'rgba(255,255,255,0.2)' }} />

      {/* User Profile */}
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Avatar
          sx={{
            width: 60,
            height: 60,
            mx: 'auto',
            mb: 1,
            bgcolor: 'rgba(255,255,255,0.2)'
          }}
        >
          <AccountCircle sx={{ fontSize: 40 }} />
        </Avatar>
        <Typography variant="body2" sx={{ opacity: 0.9 }}>
          {user?.name || 'Unknown User'}
        </Typography>
        <Typography variant="caption" sx={{ opacity: 0.7 }}>
          {new Date().toLocaleString('ko-KR')}
        </Typography>
      </Box>

      <Divider sx={{ borderColor: 'rgba(255,255,255,0.2)' }} />

      {/* Navigation Menu */}
      <List sx={{ pt: 2 }}>
        {menuItems.map((item) => (
          <ListItem key={item.id} disablePadding>
            <ListItemButton
              onClick={() => handlePageChange(item.id)}
              sx={{
                mx: 1,
                mb: 0.5,
                borderRadius: 2,
                bgcolor: selectedPage === item.id ? 'rgba(255,255,255,0.2)' : 'transparent',
                '&:hover': {
                  bgcolor: 'rgba(255,255,255,0.1)',
                },
              }}
            >
              <ListItemIcon sx={{ color: 'white', minWidth: 40 }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText
                primary={item.label}
                primaryTypographyProps={{
                  fontSize: '0.9rem',
                  fontWeight: selectedPage === item.id ? 600 : 400,
                }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>

      {/* Logout Button */}
      <Box sx={{ position: 'absolute', bottom: 16, left: 8, right: 8 }}>
        <Button
          fullWidth
          variant="outlined"
          startIcon={<Logout />}
          onClick={handleLogout}
          sx={{
            borderColor: 'rgba(255,255,255,0.3)',
            color: 'white',
            borderRadius: 2,
            '&:hover': {
              borderColor: 'rgba(255,255,255,0.5)',
              bgcolor: 'rgba(255,255,255,0.1)',
            },
          }}
        >
          로그아웃
        </Button>
      </Box>
    </Box>
  );

  const CurrentPageComponent = menuItems.find(item => item.id === selectedPage)?.component || DashboardPage;

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ display: 'flex' }}>
        {/* Top App Bar (Mobile) */}
        <AppBar
          position="fixed"
          sx={{
            width: { sm: `calc(100% - ${drawerWidth}px)` },
            ml: { sm: `${drawerWidth}px` },
            display: { sm: 'none' },
            bgcolor: 'primary.main',
          }}
        >
          <Toolbar>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
            <Typography variant="h6" noWrap component="div">
              SMART FARM
            </Typography>
            <Box sx={{ flexGrow: 1 }} />
            <Typography variant="body2" sx={{ mr: 2 }}>
              {user?.name || 'SMARTFARM 01'}
            </Typography>
            <IconButton color="inherit">
              <NotificationsNone />
            </IconButton>
          </Toolbar>
        </AppBar>

        {/* Sidebar Drawer */}
        <Box
          component="nav"
          sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
        >
          {/* Mobile drawer */}
          <Drawer
            variant="temporary"
            open={mobileOpen}
            onClose={handleDrawerToggle}
            ModalProps={{
              keepMounted: true, // Better open performance on mobile.
            }}
            sx={{
              display: { xs: 'block', sm: 'none' },
              '& .MuiDrawer-paper': {
                boxSizing: 'border-box',
                width: drawerWidth,
              },
            }}
          >
            {drawer}
          </Drawer>

          {/* Desktop drawer */}
          <Drawer
            variant="permanent"
            sx={{
              display: { xs: 'none', sm: 'block' },
              '& .MuiDrawer-paper': {
                boxSizing: 'border-box',
                width: drawerWidth,
                border: 'none',
              },
            }}
            open
          >
            {drawer}
          </Drawer>
        </Box>

        {/* Main Content */}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            width: { sm: `calc(100% - ${drawerWidth}px)` },
            minHeight: '100vh',
            bgcolor: 'background.default',
            pt: { xs: 7, sm: 0 }, // Account for mobile app bar
          }}
        >
          {/* Top Header (Desktop) */}
          <Box
            sx={{
              display: { xs: 'none', sm: 'flex' },
              justifyContent: 'space-between',
              alignItems: 'center',
              px: 3,
              py: 2,
              bgcolor: 'background.paper',
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Typography variant="h6" sx={{ color: 'text.primary' }}>
              {menuItems.find(item => item.id === selectedPage)?.label}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {user?.name || 'SMARTFARM 01'}
              </Typography>
              <IconButton>
                <NotificationsNone />
              </IconButton>
              <Button
                variant="outlined"
                startIcon={<Logout />}
                onClick={handleLogout}
                size="small"
                sx={{ borderRadius: 2 }}
              >
                로그아웃
              </Button>
            </Box>
          </Box>

          {/* Page Content */}
          <Box sx={{ p: 3 }}>
            <CurrentPageComponent />
          </Box>
        </Box>
      </Box>
    </ThemeProvider>
  );
}

const App: React.FC = () => {
  return (
    <AuthProvider>
      <MainApp />
    </AuthProvider>
  );
};

export default App;